import { useState } from 'react';
import { aboutContent, siteConfig } from '@/mocks/videos';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setIsSubmitting(true);
    setFormError('');

    try {
      formData.delete('phone_alt');
      const res = await fetch('https://readdy.ai/api/form/d9srfnh4ne17ei0sdmo0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const code = (parsed?.code as string) || '';
      const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string || (parsed?.message as string) || responseText;

      if (res.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main>
      <Breadcrumb items={breadcrumbItems} />

      {/* Page Header */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 text-center">
        <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
          关于日韩视频精选合集
        </h1>
        <p className="text-foreground-600 text-sm max-w-2xl mx-auto">
          {siteConfig.slogan}，为日韩文化爱好者提供一站式日韩视频观看体验
        </p>
      </section>

      {/* Brand Introduction */}
      <section className="w-full px-4 md:px-6 py-12 bg-background-100">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
            {aboutContent.brandIntro.title}
          </h2>
          <div className="space-y-4">
            {aboutContent.brandIntro.paragraphs.map((p, idx) => (
              <p key={idx} className="text-sm text-foreground-700 leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="w-full px-4 md:px-6 py-12">
        <div className="max-w-[1000px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {aboutContent.reputation.stats.map((stat, idx) => (
              <div key={idx} className="text-center p-6 rounded-xl bg-background-100">
                <div className="text-3xl font-bold text-primary-500 font-heading mb-1">{stat.value}</div>
                <div className="text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section className="w-full px-4 md:px-6 py-12 bg-background-100">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
            {aboutContent.reputation.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {aboutContent.reputation.testimonials.map((t, idx) => (
              <div key={idx} className="bg-background-50 rounded-xl p-6">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm w-4 h-4 flex items-center justify-center"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.comment}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <span className="text-sm font-medium text-foreground-900">{t.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full px-4 md:px-6 py-12">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
            {aboutContent.contact.title}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Contact Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-500">地址</div>
                  <div className="text-sm text-foreground-900 font-medium">{aboutContent.contact.address}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-500">邮箱</div>
                  <div className="text-sm text-foreground-900 font-medium">{aboutContent.contact.email}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-500">电话</div>
                  <div className="text-sm text-foreground-900 font-medium">{aboutContent.contact.phone}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-wechat-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-500">微信</div>
                  <div className="text-sm text-foreground-900 font-medium">{aboutContent.contact.wechat}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 rounded-xl bg-background-100">
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-primary-500 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <div className="text-xs text-foreground-500">工作时间</div>
                  <div className="text-sm text-foreground-900 font-medium">{aboutContent.contact.hours}</div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-background-100 rounded-xl p-6">
              <h3 className="font-semibold text-foreground-950 mb-4 text-sm">发送消息</h3>
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-white border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-white border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-white border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入消息主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">消息</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg bg-white border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的消息（最多500字）"
                  ></textarea>
                </div>

                {/* Honeypot */}
                <div className="hp-field">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? '发送中...' : '发送消息'}
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-green-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-500 text-center">{formError}</p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "关于日韩视频精选合集",
          "description": "日韩视频精选合集，提供日韩电影、电视剧、综艺、音乐MV等高清在线观看服务",
          "mainEntity": {
            "@type": "Organization",
            "name": "日韩视频精选合集",
            "description": "日韩视频精选合集，提供日韩电影、电视剧、综艺、音乐MV等高清在线观看服务",
            "url": "https://jp-kr-video.example.com",
            "email": aboutContent.contact.email,
            "telephone": aboutContent.contact.phone,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": aboutContent.contact.address
            }
          }
        }),
      }} />
    </main>
  );
}